import React, { useState } from 'react';
import './SMSCampaign.scss';

const SMSCampaign = ({ onClose, onSave, editingCampaign = null }) => {
  const [campaignData, setCampaignData] = useState({
    name: editingCampaign?.name || '',
    message: editingCampaign?.message || '',
    fromNumber: editingCampaign?.fromNumber || '',
    segment: editingCampaign?.segment || 'all_candidates',
    scheduledAt: editingCampaign?.scheduledAt || '',
    isScheduled: editingCampaign?.isScheduled || false,
    template: editingCampaign?.template || 'custom'
  });

  const [activeTab, setActiveTab] = useState('content');
  const [charCount, setCharCount] = useState(campaignData.message.length);

  // SMS templates
  const smsTemplates = {
    interview_reminder: {
      name: 'Interview Reminder',
      message: `Hi {{first_name}}! Reminder: You have an interview with {{company_name}} scheduled for {{interview_date}} at {{interview_time}}. Please confirm by replying YES. Location: {{interview_location}}`
    },
    application_confirmation: {
      name: 'Application Received',
      message: `Hi {{first_name}}! We've received your application for {{position_name}} at {{company_name}}. We'll review it and get back to you within 3-5 business days. Thanks!`
    },
    interview_invite: {
      name: 'Interview Invitation',
      message: `Hi {{first_name}}! We'd like to invite you for an interview for the {{position_name}} position. Are you available on {{interview_date}}? Reply YES to confirm or suggest another date.`
    },
    offer_notification: {
      name: 'Offer Extended',
      message: `Congratulations {{first_name}}! We're pleased to extend an offer for {{position_name}} at {{company_name}}. Please check your email for details. Reply ACK to confirm receipt.`
    },
    status_update: {
      name: 'Status Update',
      message: `Hi {{first_name}}, update on your {{position_name}} application: {{status_message}}. Check your email for more details. Questions? Reply to this message.`
    },
    follow_up: {
      name: 'Follow-up Message',
      message: `Hi {{first_name}}! Just checking in about your {{position_name}} application with {{company_name}}. We're still reviewing applications and will update you soon. Thanks for your patience!`
    }
  };

  // Candidate segments
  const segmentOptions = [
    { value: 'all_candidates', label: 'All Candidates', count: 1234 },
    { value: 'new_applicants', label: 'New Applicants (Last 7 days)', count: 89 },
    { value: 'interview_scheduled', label: 'Interview Scheduled', count: 45 },
    { value: 'interview_today', label: 'Interview Today', count: 8 },
    { value: 'interview_tomorrow', label: 'Interview Tomorrow', count: 12 },
    { value: 'pending_response', label: 'Pending Response', count: 156 },
    { value: 'offer_pending', label: 'Offer Pending', count: 23 },
    { value: 'active_prospects', label: 'Active Prospects', count: 234 }
  ];

  // Available merge tags
  const mergeTags = [
    { tag: '{{first_name}}', description: 'Candidate\'s first name' },
    { tag: '{{last_name}}', description: 'Candidate\'s last name' },
    { tag: '{{full_name}}', description: 'Candidate\'s full name' },
    { tag: '{{position_name}}', description: 'Applied position title' },
    { tag: '{{company_name}}', description: 'Your company name' },
    { tag: '{{interview_date}}', description: 'Interview date' },
    { tag: '{{interview_time}}', description: 'Interview time' },
    { tag: '{{interview_location}}', description: 'Interview location' },
    { tag: '{{application_id}}', description: 'Application reference' },
    { tag: '{{status_message}}', description: 'Current status message' }
  ];

  const handleSave = () => {
    if (!campaignData.name.trim()) {
      alert('Please enter a campaign name');
      return;
    }
    if (!campaignData.message.trim()) {
      alert('Please enter SMS message');
      return;
    }
    if (campaignData.message.length > 160) {
      alert('SMS message must be 160 characters or less');
      return;
    }

    onSave({
      ...campaignData,
      type: 'sms',
      createdAt: new Date().toISOString()
    });
  };

  const handleTemplateSelect = (templateKey) => {
    const template = smsTemplates[templateKey];
    setCampaignData(prev => ({
      ...prev,
      template: templateKey,
      message: template.message
    }));
    setCharCount(template.message.length);
  };

  const handleMessageChange = (e) => {
    const message = e.target.value;
    if (message.length <= 160) {
      setCampaignData(prev => ({
        ...prev,
        message: message
      }));
      setCharCount(message.length);
    }
  };

  const insertMergeTag = (tag) => {
    const textarea = document.getElementById('sms-message');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newMessage = campaignData.message.substring(0, start) + 
                        tag + 
                        campaignData.message.substring(end);
      
      if (newMessage.length <= 160) {
        setCampaignData(prev => ({
          ...prev,
          message: newMessage
        }));
        setCharCount(newMessage.length);
        
        // Reset cursor position
        setTimeout(() => {
          textarea.selectionStart = textarea.selectionEnd = start + tag.length;
          textarea.focus();
        }, 0);
      } else {
        alert('Adding this tag would exceed the 160 character limit');
      }
    }
  };

  const getEstimatedReach = () => {
    const segment = segmentOptions.find(s => s.value === campaignData.segment);
    return segment ? segment.count : segmentOptions[0]?.count || 0;
  };

  const getMessageParts = () => {
    return Math.ceil(campaignData.message.length / 160);
  };

  return (
    <div className="sms-campaign-modal">
      <div className="sms-campaign-container">
        {/* Header */}
        <div className="campaign-header">
          <div className="header-content">
            <h2>📱 {editingCampaign ? 'Edit SMS Campaign' : 'Create SMS Campaign'}</h2>
            <p>Send targeted SMS messages to engage candidates instantly</p>
          </div>
          <div className="header-actions">
            <button className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSave}>
              {editingCampaign ? 'Update Campaign' : 'Save Campaign'}
            </button>
          </div>
        </div>

        <div className="campaign-content">
          {/* Navigation Tabs */}
          <div className="campaign-tabs">
            <button
              className={`tab-btn ${activeTab === 'content' ? 'active' : ''}`}
              onClick={() => setActiveTab('content')}
            >
              📝 Content
            </button>
            <button
              className={`tab-btn ${activeTab === 'audience' ? 'active' : ''}`}
              onClick={() => setActiveTab('audience')}
            >
              🎯 Audience
            </button>
            <button
              className={`tab-btn ${activeTab === 'schedule' ? 'active' : ''}`}
              onClick={() => setActiveTab('schedule')}
            >
              📅 Schedule
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'content' && (
            <div className="tab-content">
              <div className="content-section">
                {/* Campaign Settings */}
                <div className="form-section">
                  <h3>Campaign Details</h3>
                  
                  <div className="form-group">
                    <label>Campaign Name *</label>
                    <input
                      type="text"
                      value={campaignData.name}
                      onChange={(e) => setCampaignData(prev => ({
                        ...prev,
                        name: e.target.value
                      }))}
                      placeholder="e.g., Interview Reminder SMS"
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label>From Number</label>
                    <input
                      type="text"
                      value={campaignData.fromNumber}
                      onChange={(e) => setCampaignData(prev => ({
                        ...prev,
                        fromNumber: e.target.value
                      }))}
                      placeholder="Your company SMS number"
                      className="form-input"
                    />
                    <small className="form-help">
                      Leave blank to use your default SMS number
                    </small>
                  </div>
                </div>

                {/* SMS Templates */}
                <div className="templates-section">
                  <h3>SMS Templates</h3>
                  <div className="templates-grid">
                    <div
                      className={`template-card ${campaignData.template === 'custom' ? 'active' : ''}`}
                      onClick={() => setCampaignData(prev => ({ ...prev, template: 'custom' }))}
                    >
                      <div className="template-icon">📝</div>
                      <div className="template-name">Custom SMS</div>
                      <div className="template-desc">Start from scratch</div>
                    </div>
                    
                    {Object.entries(smsTemplates).map(([key, template]) => (
                      <div
                        key={key}
                        className={`template-card ${campaignData.template === key ? 'active' : ''}`}
                        onClick={() => handleTemplateSelect(key)}
                      >
                        <div className="template-icon">📱</div>
                        <div className="template-name">{template.name}</div>
                        <div className="template-desc">Pre-built template</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* SMS Message Editor */}
                <div className="message-editor-section">
                  <div className="message-header">
                    <h3>SMS Message</h3>
                    <div className="message-stats">
                      <span className={`char-count ${charCount > 160 ? 'exceeded' : ''}`}>
                        {charCount}/160 characters
                      </span>
                      <span className="message-parts">
                        {getMessageParts()} message{getMessageParts() > 1 ? 's' : ''}
                      </span>
                    </div>
                  </div>

                  <div className="message-editor">
                    <div className="editor-toolbar">
                      <div className="merge-tags-dropdown">
                        <button className="merge-tags-btn">
                          + Insert Variable
                        </button>
                        <div className="merge-tags-list">
                          {mergeTags.map(tag => (
                            <div
                              key={tag.tag}
                              className="merge-tag-item"
                              onClick={() => insertMergeTag(tag.tag)}
                            >
                              <code>{tag.tag}</code>
                              <span>{tag.description}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    
                    <div className="form-group">
                      <textarea
                        id="sms-message"
                        value={campaignData.message}
                        onChange={handleMessageChange}
                        placeholder="Enter your SMS message here..."
                        className="form-textarea sms-message"
                        rows="4"
                        maxLength="160"
                      />
                    </div>
                    
                    {charCount > 160 && (
                      <div className="message-warning">
                        ⚠️ Message exceeds 160 characters and will be sent as {getMessageParts()} separate messages
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'audience' && (
            <div className="tab-content">
              <div className="audience-section">
                <h3>Select Audience</h3>
                
                <div className="segments-list">
                  {segmentOptions.map(segment => (
                    <div
                      key={segment.value}
                      className={`segment-item ${campaignData.segment === segment.value ? 'selected' : ''}`}
                      onClick={() => setCampaignData(prev => ({
                        ...prev,
                        segment: segment.value
                      }))}
                    >
                      <div className="segment-info">
                        <div className="segment-name">{segment.label}</div>
                        <div className="segment-description">
                          {segment.count} candidates will receive this SMS
                        </div>
                      </div>
                      <div className="segment-count">{segment.count}</div>
                    </div>
                  ))}
                </div>

                <div className="audience-summary">
                  <div className="summary-card">
                    <div className="summary-icon">🎯</div>
                    <div className="summary-info">
                      <div className="summary-title">Estimated Reach</div>
                      <div className="summary-value">{getEstimatedReach().toLocaleString()} candidates</div>
                    </div>
                  </div>
                  
                  <div className="cost-estimate">
                    <div className="cost-icon">💰</div>
                    <div className="cost-info">
                      <div className="cost-title">Estimated Cost</div>
                      <div className="cost-value">
                        ${(getEstimatedReach() * getMessageParts() * 0.0075).toFixed(2)}
                      </div>
                      <div className="cost-breakdown">
                        {getEstimatedReach()} recipients × {getMessageParts()} message{getMessageParts() > 1 ? 's' : ''} × $0.0075
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'schedule' && (
            <div className="tab-content">
              <div className="schedule-section">
                <h3>Send Options</h3>
                
                <div className="send-options">
                  <div
                    className={`send-option ${!campaignData.isScheduled ? 'selected' : ''}`}
                    onClick={() => setCampaignData(prev => ({ ...prev, isScheduled: false }))}
                  >
                    <div className="option-radio">
                      <div className={`radio ${!campaignData.isScheduled ? 'checked' : ''}`}></div>
                    </div>
                    <div className="option-info">
                      <div className="option-title">Send Immediately</div>
                      <div className="option-desc">SMS will be sent right after saving</div>
                    </div>
                  </div>
                  
                  <div
                    className={`send-option ${campaignData.isScheduled ? 'selected' : ''}`}
                    onClick={() => setCampaignData(prev => ({ ...prev, isScheduled: true }))}
                  >
                    <div className="option-radio">
                      <div className={`radio ${campaignData.isScheduled ? 'checked' : ''}`}></div>
                    </div>
                    <div className="option-info">
                      <div className="option-title">Schedule for Later</div>
                      <div className="option-desc">Choose a specific date and time</div>
                    </div>
                  </div>
                </div>

                {campaignData.isScheduled && (
                  <div className="schedule-datetime">
                    <div className="form-group">
                      <label>Scheduled Date & Time</label>
                      <input
                        type="datetime-local"
                        value={campaignData.scheduledAt}
                        onChange={(e) => setCampaignData(prev => ({
                          ...prev,
                          scheduledAt: e.target.value
                        }))}
                        min={new Date().toISOString().slice(0, 16)}
                        className="form-input"
                      />
                    </div>
                  </div>
                )}

                <div className="schedule-info">
                  <div className="info-card">
                    <h4>📱 SMS Best Practices</h4>
                    <ul className="best-practices">
                      <li>Send between 9 AM - 6 PM in recipient's timezone</li>
                      <li>Avoid sending on weekends unless urgent</li>
                      <li>Include opt-out instructions (Reply STOP)</li>
                      <li>Keep messages clear and actionable</li>
                      <li>Always identify your company</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default SMSCampaign;